/**
 * The one way product code schedules work on a timer (#12187).
 *
 * Every product timer goes through here, so a story can own it: the page's
 * timers are resolved at call time, which is what lets `withProductClock`
 * (test-utils/harness/productClock.ts) move a debounce, an auto-dismiss or a
 * poll forward by exactly its own duration instead of the story waiting real
 * time for it and racing a slow runner. One entry point is also the seam for
 * a clock that drives product timers alone, leaving the test's own waits on
 * real time.
 *
 * `tests/harness/productTimers.test.ts` fails any `setTimeout`,
 * `setInterval`, `clearTimeout` or `clearInterval` called directly in product
 * code. `requestAnimationFrame` is not a timer in this sense: it is paced by
 * the display, and `nextFrame` / `framesSettled` already wait for it.
 */

/** A timer handle. The browser's, which is a number, not Node's object. */
export type ProductTimer = number;

// The DOM's own signatures, so the handle is a number in every file whatever
// other timer typings the project loads. Read at call time, not captured at
// import: a fake clock installed after this module loaded must still be the
// one that runs the callback.
const timers = (): WindowOrWorkerGlobalScope =>
  globalThis as unknown as WindowOrWorkerGlobalScope;

/** Run `callback` once after `ms`. */
export const setProductTimeout = (
  callback: () => void,
  ms?: number
): ProductTimer => timers().setTimeout(callback, ms);

/** Cancel a timeout. A missing handle is a no-op, as with `clearTimeout`. */
export const clearProductTimeout = (
  timer: ProductTimer | null | undefined
): void => {
  if (timer !== null && timer !== undefined) timers().clearTimeout(timer);
};

/** Run `callback` every `ms` until cleared. */
export const setProductInterval = (
  callback: () => void,
  ms?: number
): ProductTimer => timers().setInterval(callback, ms);

/** Cancel an interval. A missing handle is a no-op. */
export const clearProductInterval = (
  timer: ProductTimer | null | undefined
): void => {
  if (timer !== null && timer !== undefined) timers().clearInterval(timer);
};

/**
 * Resolve after `ms`. For spacing out retries only: never to wait for
 * something to happen, which has a signal of its own (a promise, an event,
 * an effect).
 */
export const productDelay = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setProductTimeout(() => resolve(), ms);
  });

export interface RetryOptions {
  /** Total attempts, the first included. */
  attempts: number;
  /** Wait before the second attempt; doubles each time after. */
  baseMs: number;
  /** Whether a failure is worth another attempt. Default: always. */
  shouldRetry?: (error: unknown, attempt: number) => boolean;
}

/** Call `task` until it resolves, backing off exponentially between tries. */
export const retryWithBackoff = async <T>(
  task: (attempt: number) => Promise<T>,
  { attempts, baseMs, shouldRetry = () => true }: RetryOptions
): Promise<T> => {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await task(attempt);
    } catch (error: unknown) {
      if (attempt + 1 >= attempts || !shouldRetry(error, attempt)) throw error;
      await productDelay(baseMs * 2 ** attempt);
    }
  }
};

/** Thrown by `withDeadline` when the work outlasts its deadline. */
export class DeadlineExceededError extends Error {
  constructor(readonly ms: number) {
    super(`Deadline of ${ms} ms exceeded`);
    this.name = "DeadlineExceededError";
  }
}

/**
 * Settle with `work`, or reject with `DeadlineExceededError` after `ms`.
 * The deadline's timer is cleared either way, so nothing outlives the call.
 */
export const withDeadline = <T>(work: Promise<T>, ms: number): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = setProductTimeout(
      () => reject(new DeadlineExceededError(ms)),
      ms
    );
    work.then(
      (value) => {
        clearProductTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearProductTimeout(timer);
        reject(error);
      }
    );
  });
